import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popconfirm } from "antd";
import {
  ArrowRight,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cpu,
  Download,
  File,
  FileText,
  FileUp,
  Globe,
  Headphones,
  Info,
  Check,
  LoaderCircle,
  LogOut,
  Mic,
  Pause,
  Play,
  Radio,
  Search,
  Subtitles,
  Trash2,
  X,
} from "lucide-react";
import { podcastVoicesApi, podcastGenerateAsyncApi, podcastGenerateAsyncWithFileApi, podcastWaitTaskApi, podcastPageApi, podcastTaskDetailApi, podcastDeleteApi, podcastTemplatesApi } from "../../service/podcast";
import type { PodcastTaskRespVO, PodcastRoundRecord, PodcastSpeakerLike } from "../../service/podcast";
import { triggerMembershipRefresh } from "../../service/membership";
import { usePodcastAudioManager, PodcastAudioSourceType } from "../../hooks/usePodcastAudioManager";

/* 兼容 speakers 为 string[] 或 {speakerId,speakerName,gender}[] */
const speakerName = (s: PodcastSpeakerLike): string => typeof s === "string" ? s : (s.speakerName || s.speakerId || "");
const speakerGender = (s: PodcastSpeakerLike): string => typeof s === "string" ? "" : (s.gender || "");
const taskDurationSec = (t: PodcastTaskRespVO): number => t.audioDurationSec ?? Math.floor((t.audioDurationMs || 0) / 1000);

/** 解析音色试听 URL：http 升级 https，相对路径拼 CDN 前缀（与 voiceAi 一致） */
const resolvePreviewUrl = (rawUrl?: string): string => {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://")) return `https://${rawUrl.slice("http://".length)}`;
  if (rawUrl.startsWith("https://")) return rawUrl;
  const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
};
import { VIDEO_CDN_PREFIX } from "../../config/video";

/** 封面兜底图：与作品/模板卡片当前使用的默认封面一致 */
const COVER_FALLBACK = "/img/aispeaker-logo.png";

/** 解析封面 URL：http 升级 https，相对路径拼 CDN 前缀；空值回退到默认封面 */
const resolveCoverUrl = (rawUrl?: string): string => {
  if (!rawUrl || !rawUrl.trim()) return COVER_FALLBACK;
  return resolvePreviewUrl(rawUrl.trim()) || COVER_FALLBACK;
};

/** 由任务数据构建详情（作品/模板共用），author 由调用方传入 */
const buildTaskDetail = (t: PodcastTaskRespVO, author: string, isTemplate: boolean): DetailData => {
  const speakerByKey = new Map<string, PodcastSpeakerLike>((t.speakers || []).map(s => {
    const key = typeof s === "string" ? s : (s.speakerId || s.speakerName || "");
    return [key, s];
  }));
  const resolveSpeaker = (key: string) => {
    const sp = speakerByKey.get(key);
    if (sp) return sp;
    return (t.speakers || []).find(s => String(typeof s === "string" ? s : s.speakerId || s.speakerName) === key);
  };
  const spColor = (s: PodcastSpeakerLike) => {
    const g = speakerGender(s);
    return g?.includes("female") ? "pink" : "blue" as const;
  };
  const lines: ScriptLine[] = (t.rounds || [])
    .filter(r => r.text)
    .map((r) => {
      const sp = resolveSpeaker(r.speaker);
      return {
        name: sp ? speakerName(sp) : r.speaker || "旁白",
        color: sp ? spColor(sp) : "blue" as const,
        text: r.text,
      };
    });
  return {
    title: t.name || t.inputText?.replace(/\s+/g, " ").slice(0, 30) || (isTemplate ? "播客模板" : "播客作品"),
    author,
    cover: resolveCoverUrl(t.coverUrl),
    coverFilled: !!t.coverUrl,
    views: String(t.viewCount ?? 0),
    date: t.createTime || "",
    total: formatTime(taskDurationSec(t)),
    isTemplate,
    voice: "音色：" + (t.speakers || []).map(s => (speakerGender(s)?.includes("female") ? "●" : "◆") + " " + speakerName(s)).join("  "),
    mode: "",
    file: t.inputFileName || "",
    type: t.inputFileName ? t.inputFileName.split(".").pop()?.toUpperCase() || "" : "",
    desc: t.inputText || "",
    reportTitle: "内容大纲",
    reportLead: t.inputText?.slice(0, 120) || "",
    bullets: [],
    lines,
  };
};
import { triggerUrlDownload } from "../../utils/batchVideoDownload";
import { globalStore } from "../../utils/globalStore";
import { getDicebearAvataaarsUrl } from "../home/dicebearAvatar";
import { APP_NAME, TAB_BRAND_ICON } from "../../config/constants";
import { AppToast } from "../../components/common/AlertMessage";
import "./Podcast.scss";

/* mini SVG icon used in cover-reuse-btn */
const ExternalIcon: FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" style={{ width: 12, height: 12 }}>
    <path d="M7 17 17 7M8 7h9v9" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Types & data                                                       */
/* ------------------------------------------------------------------ */

interface SelectOption {
  id: string;
  name: string;
  desc: string;
  avatar: string;
  tag: string;
  color?: string;
}

type SelectType = "duration" | "language" | "model";

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  url?: string;
  file?: File;
}

interface ScriptLine {
  name: string;
  color: "blue" | "pink";
  text: string;
}

interface DetailData {
  title: string;
  author: string;
  views: string;
  date: string;
  total: string;
  isTemplate?: boolean;
  cover?: string;
  coverFilled?: boolean;
  voice: string;
  mode: string;
  file: string;
  type: string;
  desc: string;
  reportTitle: string;
  reportLead: string;
  bullets: string[];
  lines: ScriptLine[];
}

interface WorkItem {
  id: string;
  taskId: string;
  title: string;
  meta: string;
  duration: number;
  searchText: string;
  status: number;
  audioUrl?: string;
  raw?: PodcastTaskRespVO;
}

const OPTIONS: Record<SelectType, SelectOption[]> = {
  duration: [
    { id: "quick", name: "速听精华", desc: "播客时长 3-5 分钟", avatar: "快", tag: "默认" },
    { id: "deep", name: "深度探索", desc: "播客时长 8-15 分钟", avatar: "深", tag: "深度" },
    { id: "debate", name: "辩论", desc: "播客时长 5-10 分钟", avatar: "辩", tag: "双人" },
  ],
  language: [
    { id: "zh", name: "中文", desc: "", avatar: "", tag: "" },
  ],
  model: [
    { id: "pro", name: "Podcast-Pro", desc: "结构、脚本与音频质量均衡", avatar: "P", tag: "推荐" },
    { id: "fast", name: "Podcast-Fast", desc: "快速生成，适合批量草稿", avatar: "F", tag: "快速" },
    { id: "studio", name: "Podcast-Studio", desc: "更强叙事和转场，适合成品输出", avatar: "S", tag: "高品质" },
  ],
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function escapeHtml(value: string): string {
  return String(value).replace(/[&<>'"]/g, (char) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char] ?? char
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
}

function durationSeconds(key: string): number {
  return key === "quick" ? 240 : key === "deep" ? 720 : 480;
}

let workIdCounter = 4;

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

const SelectPopover: FC<{
  type: SelectType;
  visible?: boolean;
  selected: Record<SelectType, string>;
  onPick: (type: SelectType, id: string) => void;
}> = ({ type, visible, selected, onPick }) => {
  if (!visible) return null;
  return (
    <div className="select-popover show">
      <div className="select-popover-list">
        {OPTIONS[type].map((item) => {
          const picked = item.id === selected[type];
          return (
            <div
              key={item.id}
              className={`pop-option${picked ? " selected" : ""}`}
              onClick={() => onPick(type, item.id)}
            >
              {item.name}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AiPodcast: FC = () => {
  const [podcastText, setPodcastText] = useState("");
  const [selected, setSelected] = useState<Record<SelectType, string>>({
    duration: "quick",
    language: "zh",
    model: "pro",
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [templates, setTemplates] = useState<PodcastTaskRespVO[]>([]);
  const [showWorks, setShowWorks] = useState(false);
  const [toastText, setToastText] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [generating, setGenerating] = useState(false);
  const generatingRef = useRef(false);
  const [activePopover, setActivePopover] = useState<SelectType | null>(null);

  // Voice popover state
  const voiceBtnRef = useRef<HTMLButtonElement>(null);
  const [voicePopoverOpen, setVoicePopoverOpen] = useState(false);
  const [podcastVoices, setPodcastVoices] = useState<Array<{ id: number; speakerId?: string; displayName: string; description?: string; gender: string; previewAudioUrl: string }>>([]);
  const [selectedVoiceIds, setSelectedVoiceIds] = useState<number[]>([]);

  // Detail state
  const [currentDetailId, setCurrentDetailId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<DetailData | null>(null);
  /** 当前详情对应的原始任务数据（作品/模板通用，导出用 audioUrl/srtUrl/scriptUrl） */
  const [detailRaw, setDetailRaw] = useState<PodcastTaskRespVO | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number>(0);

  // 页面级单 Audio Owner（隐式互斥：WORK/TEMPLATE/PREVIEW/DETAIL 全部走同一个 audio）
  const audioManager = usePodcastAudioManager();
  const { toggle, stop, seek, current, playing, currentTime, progress } = audioManager;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const detailScrollRef = useRef<HTMLDivElement | null>(null);

  const charCount = podcastText.length;
  /** 可生成条件：恰好选择 2 个音色，且已输入文字或上传文件 */
  const canGenerate = selectedVoiceIds.length === 2 && (podcastText.trim().length > 0 || uploadedFiles.length > 0);
  const notify = useCallback((message: string) => {
    setToastText(message);
    setToastVisible(true);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToastVisible(false), 2200);
  }, []);

  const handlePreviewVoice = useCallback((previewUrl: string, previewKey: string) => {
    if (!previewUrl) {
      notify("当前音色暂无可用试听音频");
      return;
    }
    void toggle(previewUrl, {
      source: { type: PodcastAudioSourceType.PREVIEW, id: previewKey },
    });
  }, [toggle, notify]);
  const currentUser = useMemo(() => {
    const u = globalStore.getUserInfo() || {};
    return { name: u.nickname || u.nickName || u.username || u.mobile || "用户", avatar: u.avatar || "" };
  }, []);

  /* ---- popover management ---- */
  const openPopover = useCallback((type: SelectType, e: React.MouseEvent) => {
    e.stopPropagation();
    setActivePopover((prev) => prev === type ? null : type);
  }, []);

  useEffect(() => {
    if (!activePopover) return;
    const close = () => setActivePopover(null);
    document.addEventListener("click", close, { once: true });
    return () => document.removeEventListener("click", close);
  }, [activePopover]);

  const pickOption = useCallback((type: SelectType, id: string) => {
    setSelected((prev) => ({ ...prev, [type]: id }));
    setActivePopover(null);
  }, []);

  /* ---- file management ---- */
  const handleFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const allowed = /\.(doc|pdf|txt)$/i;
    const files = Array.from(e.target.files || []);
    const rejected: string[] = [];
    const oversized: string[] = [];
    const newFiles: UploadedFile[] = [];
    files.forEach((file) => {
      if (!allowed.test(file.name)) { rejected.push(file.name); return; }
      if (file.size > 10 * 1024 * 1024) { oversized.push(file.name); return; }
      newFiles.push({ id: `file-${Date.now()}-${Math.random().toString(16).slice(2)}`, name: file.name, size: file.size, file });
    });
    if (e.target) e.target.value = "";
    if (newFiles.length) setUploadedFiles(newFiles);
    if (oversized.length) notify("上传文件不能超过10M");
    if (rejected.length) alert(`仅支持上传 doc、pdf、txt 格式：\n${rejected.join("\n")}`);
  }, [notify]);

  const removeFile = useCallback((id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearText = useCallback(() => {
    setPodcastText("");
    setUploadedFiles([]);
  }, []);

  /* 应用作品或模板：回填文本、音色和原始文件配置 */
  const applyTaskConfiguration = useCallback((task: PodcastTaskRespVO) => {
    if (task.inputText) setPodcastText(task.inputText);
    const fileUrl = task.inputFileUrl || task.inputUrl;
    if (fileUrl) {
      const fileName = task.inputFileName || fileUrl.split("/").pop()?.split("?")[0] || "file";
      setUploadedFiles([{
        id: `applied-file-${task.taskId}`,
        name: fileName,
        size: task.inputFileSizeBytes || 0,
        url: fileUrl,
      }]);
    } else {
      setUploadedFiles([]);
    }
    if (task.speakers?.length) {
      const ids = task.speakers
        .map(s => {
          const nm = speakerName(s);
          const key = typeof s === "string" ? s : (s.speakerId || s.speakerName || "");
          return podcastVoices.find(v => String(v.id) === key || v.speakerId === key || v.displayName === nm || v.displayName === key)?.id;
        })
        .filter((n): n is number => n != null);
      if (ids.length > 0) setSelectedVoiceIds(ids);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => (document.querySelector(".podcast-textarea") as HTMLTextAreaElement | null)?.focus(), 350);
  }, [podcastVoices]);

  /* ---- generate ---- */
  const generatePodcast = useCallback(async () => {
    if (generatingRef.current) return;
    if (selectedVoiceIds.length !== 2) {
      notify("请选择两个音色后尝试");
      return;
    }
    if (!podcastText.trim() && uploadedFiles.length === 0) {
      notify("请先输入内容或上传文档");
      return;
    }
    generatingRef.current = true;
    setGenerating(true);
    setShowWorks(true);
    window.setTimeout(() => triggerMembershipRefresh(), 2000);
    try {
      const speakers = selectedVoiceIds.map(id => podcastVoices.find(v => v.id === id)?.speakerId || String(id));
      const uploadedFile = uploadedFiles.find(f => f.file);
      const savedFileUrl = uploadedFiles.find(f => f.url && !f.file)?.url;
      const reqBody = {
        action: 0,
        inputText: podcastText.trim() || undefined,
        inputUrl: savedFileUrl,
        speakers,
        audioFormat: "mp3",
        sampleRate: 24000,
        speechRate: 0,
      };
      const res = uploadedFile?.file
        ? await podcastGenerateAsyncWithFileApi(JSON.stringify(reqBody), uploadedFile.file)
        : await podcastGenerateAsyncApi(reqBody);
      if (res.code === 0 && res.data) {
        const task = res.data;
        const work: WorkItem = {
          id: task.taskId,
          taskId: task.taskId,
          title: task.name || podcastText.replace(/\s+/g, " ").slice(0, 30) || "新播客作品",
          meta: `${(task.speakers || []).map(speakerName).join(" && ")} / ${formatTime(taskDurationSec(task))}`,
          duration: taskDurationSec(task),
          searchText: task.inputText || "",
          status: task.status,
          raw: task,
        };
        setWorks((prev) => [work, ...prev]);
        // 开始轮询
        void pollPodcastTask(task.taskId);
      } else {
        generatingRef.current = false;
        setGenerating(false);
      }
    } catch {
      setGenerating(false);
    }
  }, [podcastText, uploadedFiles, selected, selectedVoiceIds, podcastVoices, notify]);

  const pollPodcastTask = useCallback(async (taskId: string) => {
    try {
      const res = await podcastWaitTaskApi(taskId, 60);
      if (res.code === 0 && res.data) {
        const task = res.data;
        setWorks((prev) => prev.map(w => w.taskId === taskId ? {
          ...w,
          status: task.status,
          title: task.name || w.title,
          duration: taskDurationSec(task),
          audioUrl: task.audioUrl,
          raw: task,
          meta: w.meta,
          searchText: w.searchText,
        } : w));
        if (task.status !== 2 && task.status !== 3) {
          // 未完成继续轮询
          setTimeout(() => void pollPodcastTask(taskId), 5000);
        } else {
          generatingRef.current = false;
          setGenerating(false);
          triggerMembershipRefresh();
          if (task.status === 2) {
            void podcastPageApi(1, 50).then((pageRes) => {
              const latestTask = pageRes.code === 0 ? pageRes.data?.list.find((item) => item.taskId === taskId) : undefined;
              if (!latestTask) return;
              const latestDuration = taskDurationSec(latestTask);
              setWorks((prev) => prev.map((work) => work.taskId === taskId ? {
                ...work,
                status: latestTask.status,
                title: latestTask.name || work.title,
                duration: latestDuration || work.duration,
                audioUrl: latestTask.audioUrl || work.audioUrl,
                raw: latestTask,
                meta: [(latestTask.speakers || []).map(speakerName).join(" && "), formatTime(latestDuration)].join(" / "),
              } : work));
            }).catch(() => {});
          }
        }
      } else {
        generatingRef.current = false;
        setGenerating(false);
      }
    } catch {
      setGenerating(false);
    }
  }, []);

  /* ---- audio playback（页面级单 Audio，见 usePodcastAudioManager） ---- */
  const handlePlayWork = useCallback((work: WorkItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!work.audioUrl) return;
    void toggle(work.audioUrl, {
      source: { type: PodcastAudioSourceType.WORK, id: work.id },
    });
  }, [toggle]);

  const handlePlayTemplate = useCallback((tmplKey: string, audioUrl: string | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioUrl) return;
    void toggle(audioUrl, {
      source: { type: PodcastAudioSourceType.TEMPLATE, id: tmplKey },
    });
  }, [toggle]);

  /* 卸载双保险：manager 内部 hook cleanup + 组件顶层显式 stop */
  useEffect(() => () => { stop(); }, [stop]);

  /* ---- delete ---- */
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const handleDeleteWork = useCallback((id: string) => {
    if (current?.type === PodcastAudioSourceType.WORK && current?.id === id) stop();
    setDeletingIds((prev) => new Set(prev).add(id));
    void podcastDeleteApi(id).then((res) => {
      if (res.code === 0) {
        setWorks((prev) => prev.filter((w) => w.id !== id));
      }
      setDeletingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }).catch(() => {
      setDeletingIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    });
  }, [current, stop]);


  /* ---- detail ---- */
  const openWorkDetail = useCallback((id: string) => {
    stop();
    setCurrentDetailId(id);
    // 先用列表缓存填 detailRaw，详情接口返回后刷新（保证导出可用 + 后端累计阅读量）
    setDetailRaw(works.find((w) => w.id === id)?.raw || null);
    void podcastTaskDetailApi(id).then((res) => {
      if (res.code === 0 && res.data) {
        setDetailRaw(res.data);
        setDetailData(buildTaskDetail(res.data, currentUser.name, false));
        setDetailOpen(true);
        setSourceOpen(false);
        setExportOpen(false);
        setTimeout(() => detailScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 0);
      }
    }).catch(() => {
      // fallback
    });
  }, [currentUser, stop, works]);

  /* 打开模板详情：显示平台 logo + Lychee Voice，每次都真实调详情接口累计阅读量 */
  const openTemplateDetail = useCallback((tmpl: PodcastTaskRespVO) => {
    stop();
    setCurrentDetailId(tmpl.taskId);
    const taskId = tmpl.taskId;
    const openWith = (t: PodcastTaskRespVO) => {
      setDetailRaw(t);
      setDetailData(buildTaskDetail(t, APP_NAME, true));
      setDetailOpen(true);
      setSourceOpen(false);
      setExportOpen(false);
      setTimeout(() => detailScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 0);
    };
    // 先用列表缓存填 detailRaw（导出可用），再真实调详情接口刷新
    setDetailRaw(tmpl);
    void podcastTaskDetailApi(taskId).then((res) => {
      if (res.code === 0 && res.data) openWith(res.data);
      else openWith(tmpl);
    }).catch(() => openWith(tmpl));
  }, [stop]);

  const closeWorkDetail = useCallback(() => {
    stop();
    setCurrentDetailId(null);
    setDetailOpen(false);
    setDetailData(null);
    setDetailRaw(null);
    setSourceOpen(false);
    setExportOpen(false);
    setCopied(false);
    window.clearTimeout(copiedTimer.current);
  }, [stop]);

  const handleToggleDetail = useCallback(() => {
    const workCard = works.find(w => w.id === currentDetailId);
    const tmpl = templates.find(t => t.taskId === currentDetailId);
    const audioUrl = workCard?.audioUrl || tmpl?.audioUrl;
    if (!audioUrl) return;
    void toggle(audioUrl, {
      source: { type: PodcastAudioSourceType.DETAIL, id: currentDetailId || "" },
    });
  }, [toggle, works, templates, currentDetailId]);

  const seekDetail = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seek(pct);
  }, [seek]);

  /* ---- source drawer ---- */
  const openSourceDrawer = useCallback(() => setSourceOpen(true), []);
  const closeSourceDrawer = useCallback(() => setSourceOpen(false), []);

  /* ---- export ---- */
  const toggleExportMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExportOpen((v) => !v);
  }, []);

  useEffect(() => {
    if (!exportOpen) return;
    const close = () => setExportOpen(false);
    document.addEventListener("click", close, { once: true });
    window.addEventListener("scroll", close, { once: true });
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close);
    };
  }, [exportOpen]);

  const chooseExport = useCallback((type: string) => {
    setExportOpen(false);
    // 作品/模板统一用当前详情的原始任务字段：audioUrl / srtUrl / scriptUrl
    const raw = detailRaw;
    if (!raw) return;
    const { taskId, srtUrl, scriptUrl, audioUrl } = raw;
    if (type === "srt" && srtUrl) {
      triggerUrlDownload(srtUrl, `podcast-${taskId}.srt`);
    } else if (type === "md" && scriptUrl) {
      triggerUrlDownload(scriptUrl, `podcast-${taskId}.md`);
    } else if (type === "audio" && audioUrl) {
      triggerUrlDownload(audioUrl, `podcast-${taskId}.mp3`);
    }
  }, [detailRaw]);

  /* ---- copy ---- */
  const copyDetailScript = useCallback(async () => {
    if (!detailData) return;
    const script = detailData.lines.map((line) => line.text).join("\n");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(script);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = script;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      textarea.remove();
      if (!ok) return;
    }
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    notify("已复制");
  }, [detailData, notify]);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  /* ---- voice popover close on outside click or scroll ---- */
  useEffect(() => {
    if (!voicePopoverOpen) return;
    const close = () => setVoicePopoverOpen(false);
    document.addEventListener("click", close, { once: true });
    window.addEventListener("scroll", close, { once: true });
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close);
    };
  }, [voicePopoverOpen]);

  /* ---- load works（keyword 可选，调后端搜索） ---- */
  const loadWorks = useCallback(async (keyword?: string) => {
    try {
      const res = await podcastPageApi(1, 50, keyword);
      if (res.code === 0 && res.data) {
        const items: WorkItem[] = res.data.list.map((t) => ({
          id: t.taskId,
          taskId: t.taskId,
          title: t.name || t.inputText?.replace(/\s+/g, " ").slice(0, 30) || "播客作品",
          meta: `${(t.speakers || []).map(speakerName).join(" && ")} / ${formatTime(taskDurationSec(t))}`,
          duration: taskDurationSec(t),
          searchText: `${t.inputText} ${(t.speakers || []).map(speakerName).join(" ")}`,
          status: t.status,
          audioUrl: t.audioUrl,
          raw: t,
        }));
        setWorks(items);
      }
    } catch { /* ignore */ }
  }, []);

  /* 搜索关键字：防抖调后端接口（模糊匹配播客名称 + 音色名）
   首次挂载由下方 [] effect 的 loadWorks() 负责，此处跳过避免重复请求 */
  const isFirstSearchRunRef = useRef(true);
  useEffect(() => {
    if (isFirstSearchRunRef.current) {
      isFirstSearchRunRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      void loadWorks(searchQuery.trim() || undefined);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery, loadWorks]);

  /* ---- load works + fetch voices on first render ---- */
  useEffect(() => {
    void loadWorks();
    void podcastVoicesApi({ provider: "volcengine" }).then((res) => {
      if (res.code === 0 && Array.isArray(res.data)) {
        const voices = res.data.map((v) => ({ id: v.id, speakerId: v.speakerId, displayName: v.displayName, description: v.description, gender: v.gender, previewAudioUrl: resolvePreviewUrl(v.previewAudioUrl) }));
        setPodcastVoices(voices);
        setSelectedVoiceIds((current) => {
          if (current.length > 0) return current;
          const normalized = (name: string) => name.replace(/\s/g, "").toLowerCase();
          const yunzhou = voices.find((voice) => normalized(voice.displayName).includes("\u4e91\u821f2.0"));
          const vivi = voices.find((voice) => normalized(voice.displayName).includes("vivi2.0"));
          return yunzhou && vivi ? [yunzhou.id, vivi.id] : current;
        });
      }
    }).catch(() => {});
    void podcastTemplatesApi().then((res) => {
      if (res.code === 0 && Array.isArray(res.data)) {
        setTemplates(res.data);
      }
    }).catch(() => {});
  }, []);

  /* ---- render ---- */
  const currentDuration = OPTIONS.duration.find((d) => d.id === selected.duration)!;
  const currentLanguage = OPTIONS.language.find((l) => l.id === selected.language)!;
  const currentModel = OPTIONS.model.find((m) => m.id === selected.model)!;
  return (
    <div className="podcast-page" style={{ minHeight: "100%", fontFamily: "'PingFang SC',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
      <div className="podcast-shell" style={detailOpen ? { paddingTop: 14 } : undefined}>
        {/* ----- List View ----- */}
        {!detailOpen && (
          <>
            <section className="studio-hero">
              <div className="section-kicker">AI Podcast Studio</div>
              <h1>AI 播客</h1>
              <p>万物皆可转译，一键开启解说</p>
            </section>

            <div className="primary-panel">
              <div className="composer-card">
                <div className={`file-strip${uploadedFiles.length ? " show" : ""}`} aria-label="已上传文件">
                  {uploadedFiles.map((f) => (
                    <span className="file-chip" title={f.name} key={f.id}>
                      <FileText size={14} />
                      <span className="file-name">{f.name}</span>
                      <button className="file-remove" type="button" onClick={() => removeFile(f.id)} aria-label="移除文件"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="textarea-wrap">
                  <textarea
                    className="podcast-textarea"
                    maxLength={1000}
                    spellCheck={false}
                    placeholder="可粘贴文章、课程资料、会议纪要或任意内容，我们会将它整理成自然的播客解说脚本并生成音频。"
                    // placeholder="可粘贴文章、课程资料、会议纪要或任意内容，我们会将它整理成自然的播客解说脚本并生成音频，文本提示词与上传文件同时传入将参考文本提示词内容生成播客。"
                    value={podcastText}
                    onChange={(e) => setPodcastText(e.target.value)}
                  />
                  <div className="textarea-counter"><span>{charCount}</span>/1000</div>
                </div>
                <div className="composer-footer">
                  <div className="composer-left">
                    {/* <div className="select-wrap">
                      <button className="inline-select-btn compact-select" type="button" onClick={(e) => openPopover("duration", e)}>
                        <Clock size={12} />
                        <span className="sel-value">{currentDuration.name}</span>
                        <ChevronDown className="sel-chevron" size={12} />
                      </button>
                      {activePopover === "duration" && <SelectPopover type="duration" visible selected={selected} onPick={pickOption} />}
                    </div>
                    <div className="select-wrap">
                      <button className="inline-select-btn compact-select" type="button" onClick={(e) => openPopover("language", e)}>
                        <Globe size={12} />
                        <span className="sel-value">{currentLanguage.name}</span>
                        <ChevronDown className="sel-chevron" size={12} />
                      </button>
                      {activePopover === "language" && <SelectPopover type="language" visible selected={selected} onPick={pickOption} />}
                    </div> */}
                    <div className="select-wrap">
                      <button
                        ref={voiceBtnRef}
                        className={`inline-select-btn voice-select-btn${voicePopoverOpen ? " active" : ""}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setVoicePopoverOpen(v => !v);
                        }}
                      >
                        <Mic size={12} />
                        <span className="sel-value">
                          {selectedVoiceIds.length === 2
                            ? podcastVoices.find(v => v.id === selectedVoiceIds[0])?.displayName + " && " + podcastVoices.find(v => v.id === selectedVoiceIds[1])?.displayName
                            : "选择音色"}
                        </span>
                        <span className="sel-label">· {selectedVoiceIds.length}/2</span>
                        <ChevronDown className="sel-chevron" size={12} />
                      </button>
                      {voicePopoverOpen && (
                      <div className="tts-popover show" onClick={(e) => e.stopPropagation()}>
                        <div className="tts-popover-list">
                          {podcastVoices.length === 0 ? (
                            <div className="tts-popover-empty">加载中...</div>
                          ) : podcastVoices.map(v => {
                            const idx = selectedVoiceIds.indexOf(v.id);
                            const isSelected = idx !== -1;
                            const genderDot = v.gender?.includes("female") ? "pink" : v.gender?.includes("male") ? "blue" : "";
                            const previewKey = `pv-${v.id}`;
                            return (
                              <div
                                key={previewKey}
                                className={`tts-popover-option${isSelected ? " selected" : ""}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedVoiceIds((prev) => prev.filter(id => id !== v.id));
                                  } else if (selectedVoiceIds.length < 2) {
                                    setSelectedVoiceIds((prev) => [...prev, v.id]);
                                  }
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    if (isSelected) {
                                      setSelectedVoiceIds((prev) => prev.filter(id => id !== v.id));
                                    } else if (selectedVoiceIds.length < 2) {
                                      setSelectedVoiceIds((prev) => [...prev, v.id]);
                                    }
                                  }
                                }}
                              >
                                <div className="tts-popover-info">
                                  <span className="tts-popover-name-row">
                                    {genderDot && <span className={`tts-popover-gender-dot ${genderDot}`} />}
                                    <span className="tts-popover-name">{v.displayName}</span>
                                  </span>
                                  {v.description ? (
                                    <span className="tts-popover-voice-description">{v.description}</span>
                                  ) : null}
                                </div>
                                <span className="tts-popover-check">
                                  {idx !== -1 && <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1a1a" }}>{idx + 1}</span>}
                                </span>
                                <button
                                  type="button"
                                  className="tts-popover-play"
                                  disabled={!v.previewAudioUrl}
                                  onClick={e => {
                                    e.stopPropagation();
                                    void handlePreviewVoice(v.previewAudioUrl, previewKey);
                                  }}
                                >
                                  {current?.type === PodcastAudioSourceType.PREVIEW && current?.id === previewKey && playing ? <Pause size={12} /> : <Play size={12} />}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    </div>
                    {/* <div className="select-wrap">
                      <button className="inline-select-btn" type="button" onClick={(e) => openPopover("model", e)}>
                        <Cpu size={15} style={{ color: "#64748b", flexShrink: 0 }} />
                        <span className="sel-value">{currentModel.name}</span>
                        <span className="sel-label">推荐</span>
                        <ChevronDown className="sel-chevron" size={14} />
                      </button>
                      {activePopover === "model" && <SelectPopover type="model" visible selected={selected} onPick={pickOption} />}
                    </div> */}
                  </div>
                  <div className="composer-right">
                    <button className="ghost-btn" type="button" onClick={clearText}>
                      <Trash2 size={14} />清空
                    </button>
                    {/* 积分扣除说明：hover 时正上方展示提示 */}
                    <span className="point-info-tip" tabIndex={0} role="img" aria-label="积分扣除说明">
                      <Info size={16} />
                      <span className="point-info-popover">
                        按照生成的音频时长扣除积分，4积分/30秒
                      </span>
                    </span>
                    <input ref={fileInputRef} type="file" accept=".doc,.pdf,.txt" style={{ display: "none" }} onChange={handleFilePick} />
                    <button className="ghost-btn icon-upload" type="button" onClick={() => fileInputRef.current?.click()}>
                      <FileUp size={16} />
                    </button>
                    <button
                      className={`generate-btn${generating ? " loading" : ""}${generating || !canGenerate ? " is-disabled" : ""}`}
                      type="button"
                      title="开始生成播客"
                      aria-label="开始生成播客"
                      aria-disabled={!canGenerate || generating}
                      disabled={generating}
                      onClick={generatePodcast}
                    >
                      {generating ? <LoaderCircle size={20} /> : <ArrowRight size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="quick-prompts">
              <div className="quick-prompt">
                <span className="quick-prompt-top"><FileText size={16} />支持传入文件 <em className="quick-prompt-formats">(支持DOC/PDF/TXT)</em></span>
                <p>上传产品白皮书或课程讲义，生成适合通勤收听的深度解说。</p>
              </div>
              <div className="quick-prompt">
                <span className="quick-prompt-top"><Globe size={16} />支持提示词</span>
                <p>粘贴文章、新闻或笔记，一键生成结构清晰的单人解说。</p>
              </div>
            </div>

            <section className="work-section">
              <div className="work-tabs">
                <button className={`work-tab${!showWorks ? " active" : ""}`} type="button" onClick={() => setShowWorks(false)}>模板</button>
                <button className={`work-tab${showWorks ? " active" : ""}`} type="button" onClick={() => setShowWorks(true)}>我的作品</button>
              </div>

              {/* Works panel */}
              <div className={`work-panel${!showWorks ? " hidden" : ""}`}>
                <div className="works-toolbar">
                  <div className="works-search">
                    <Search size={16} />
                    <input type="text" placeholder="搜索作品标题、音色" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                  </div>
                </div>
                <div className="history-grid">
                  {works.length > 0 ? works.map((work) => (
                    <article
                      key={work.id}
                      className={`podcast-card history-card${current?.type === PodcastAudioSourceType.WORK && current?.id === work.id ? " playing" : ""}`}
                      onClick={() => openWorkDetail(work.id)}
                    >
                      <div className={`podcast-cover podcast-card-cover${work.raw?.coverUrl ? " filled" : ""}`}>
                        <img className={`podcast-cover-image${work.raw?.coverUrl ? " filled" : ""}`} src={resolveCoverUrl(work.raw?.coverUrl)} alt="" aria-hidden="true" />
                        {work.status === 2 && (
                          <button
                            className="cover-reuse-btn"
                            type="button"
                            title="应用此配置"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (work.raw) applyTaskConfiguration(work.raw);
                            }}
                          >
                            <ExternalIcon />
                          </button>
                        )}
                        <span className={`cover-status-badge ${work.status === 0 ? "pending" : work.status === 1 ? "processing" : work.status === 2 ? "success" : "failed"}`}>
                          {work.status === 0 ? "待生成" : work.status === 1 ? "生成中" : work.status === 2 ? "已完成" : "失败"}
                        </span>
                        <Popconfirm
                          title="确定要删除这个作品吗？"
                          okText="确定"
                          cancelText="取消"
                          onPopupClick={(e) => e.stopPropagation()}
                          onConfirm={() => handleDeleteWork(work.id)}
                        >
                          <button
                            className="cover-delete-btn"
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="删除"
                            disabled={deletingIds.has(work.id)}
                          >
                            <Trash2 size={12} />
                          </button>
                        </Popconfirm>
                      </div>
                      <div className="card-body">
                        <div className="podcast-title-row">
                          <h3 className="podcast-title" title={work.title}>{work.title}</h3>
                          <button
                            className="round-action history-play"
                            type="button"
                            onClick={(e) => handlePlayWork(work, e)}
                          >
                            {current?.type === PodcastAudioSourceType.WORK && current?.id === work.id && playing ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                        </div>
                        <div className="podcast-meta"><span>{work.meta}</span></div>
                        <div className="history-card-player">
                          {/* 隐藏进度条 — 保留时间 */}
                          {false && (
                            <div className="history-progress">
                              <div className="history-progress-fill" style={{ width: current?.type === PodcastAudioSourceType.WORK && current?.id === work.id ? `${progress * 100}%` : "0%" }} />
                            </div>
                          )}
                          <span className="history-time">{formatTime(work.duration)}</span>
                        </div>
                      </div>
                    </article>
                  )) : (
                    <div className="empty-state">还没有作品。在上方输入内容，生成第一个播客。</div>
                  )}
                </div>
              </div>

              {/* Templates panel */}
              <div className={`work-panel${showWorks ? " hidden" : ""}`}>
                <div className="template-grid">
                  {templates.length > 0 ? templates.map((tmpl) => {
                    const tmplKey = tmpl.taskId;
                    const tmplTitle = tmpl.name || tmpl.inputText?.replace(/\s+/g, " ").slice(0, 20) || "播客模板";
                    const tmplMeta = `${(tmpl.speakers || []).map(speakerName).join(" && ")} / ${formatTime(taskDurationSec(tmpl))}`;
                    const tmplDur = taskDurationSec(tmpl);
                    return (
                      <article
                        key={tmplKey}
                        className="podcast-card history-card template-card"
                        onClick={() => openTemplateDetail(tmpl)}
                      >
                        <div className={`podcast-cover podcast-card-cover${tmpl.coverUrl ? " filled" : ""}`}>
                          <img className={`podcast-cover-image${tmpl.coverUrl ? " filled" : ""}`} src={resolveCoverUrl(tmpl.coverUrl)} alt="" aria-hidden="true" />
                          <button
                            className="cover-reuse-btn"
                            type="button"
                            title="应用此模板"
                            onClick={(e) => {
                              e.stopPropagation();
                              applyTaskConfiguration(tmpl);
                            }}
                          >
                            <ExternalIcon />
                          </button>
                        </div>
                        <div className="card-body">
                          <div className="podcast-title-row">
                            <h3 className="podcast-title" title={tmplTitle}>{tmplTitle}</h3>
                            <button className="round-action history-play" type="button" onClick={(e) => handlePlayTemplate(String(tmplKey), tmpl.audioUrl, e)}>
                              {current?.type === PodcastAudioSourceType.TEMPLATE && current?.id === String(tmplKey) && playing ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                          </div>
                          <div className="podcast-meta"><span>{tmplMeta}</span></div>
                          <div className="history-card-player">
                            {/* 隐藏进度条 — 保留时间 */}
                            {false && (
                              <div className="history-progress"><div className="history-progress-fill" style={{ width: current?.type === PodcastAudioSourceType.TEMPLATE && current?.id === String(tmplKey) ? `${progress * 100}%` : "0%" }} /></div>
                            )}
                            <span className="history-time">{formatTime(tmplDur)}</span>
                          </div>
                        </div>
                      </article>
                    );
                  }) : <div className="empty-state">暂无模板</div>}
                </div>
              </div>
            </section>
          </>
        )}

        {/* ----- Detail View ----- */}
        {detailOpen && detailData && (
          <section className="podcast-detail" id="podcastDetail" style={{ display: "block" }} ref={detailScrollRef}>
            <div className="detail-topbar">
              <button className="detail-icon-btn" type="button" onClick={closeWorkDetail} title="返回" aria-label="返回">
                <ChevronLeft size={24} />
              </button>
              <div className="export-wrap">
                <button className="export-btn" type="button" onClick={toggleExportMenu}>
                  <Download size={16} />导出
                </button>
                <div className={`export-menu${exportOpen ? " show" : ""}`}>
                  <button className="export-option" type="button" onClick={() => chooseExport("audio")}><Radio size={17} />导出音频</button>
                  <button className="export-option" type="button" onClick={() => chooseExport("srt")}><Subtitles size={17} />导出字幕 (SRT)</button>
                  <button className="export-option" type="button" onClick={() => chooseExport("md")}><FileText size={17} />导出脚本 (Markdown)</button>
                </div>
              </div>
            </div>
            <div className="detail-layout">
              <article className="detail-main">
                <div className={`detail-cover${detailData.coverFilled ? " filled" : ""}`}>
                  <img className={`podcast-cover-image detail-cover-image${detailData.coverFilled ? " filled" : ""}`} src={detailData.cover || COVER_FALLBACK} alt="" />
                </div>
                <h1 className="detail-title">{detailData.title}</h1>
                <div className="detail-meta">
                  <span className="detail-author">
                    {detailData.isTemplate ? (
                      <img className="detail-avatar" src={TAB_BRAND_ICON} alt="" />
                    ) : (
                      <img className="detail-avatar" src={getDicebearAvataaarsUrl(globalStore.getUserInfo())} alt="" />
                    )}
                    <span>{detailData.author}</span>
                  </span>
                  <span className="detail-meta-item">
                    <Headphones size={14} /><span>{detailData.views}</span>
                  </span>
                  <span>{detailData.date}</span>
                  <button className="source-pill" type="button" onClick={openSourceDrawer}>
                    来源<ChevronRight size={12} />
                  </button>
                </div>
                <button className="detail-play-btn" type="button" onClick={handleToggleDetail} aria-label="播放">
                  {current?.type === PodcastAudioSourceType.DETAIL && current?.id === (currentDetailId || "") && playing ? <Pause size={24} /> : <Play size={24} />}
                </button>
                <div className="detail-player">
                  <span>{formatTime(Math.floor(current?.type === PodcastAudioSourceType.DETAIL && current?.id === (currentDetailId || "") ? currentTime : 0))}</span>
                  <div className="detail-bar" onClick={seekDetail}>
                    <div className="detail-bar-fill" style={{ width: `${current?.type === PodcastAudioSourceType.DETAIL && current?.id === (currentDetailId || "") ? progress * 100 : 0}%` }} />
                  </div>
                  <span>{detailData.total}</span>
                </div>
                <div className="script-card">
                  <div className="script-toolbar">
                    <span className="voice-pill">
                      {detailData.voice.split(/([◆●])/).map((part, i) => {
                        if (part === "◆") return <span key={i} className="role-dot blue" />;
                        if (part === "●") return <span key={i} className="role-dot pink" />;
                        return part ? <span key={i}>{part}</span> : null;
                      })}
                    </span>
                    <button
                      className={`copy-btn${copied ? " is-copied" : ""}`}
                      type="button"
                      title={copied ? "已复制" : "复制"}
                      onClick={copyDetailScript}
                    >
                      {copied ? "已复制" : "复制"}
                    </button>
                  </div>
                  <div className="script-lines">
                    {detailData.lines.map((line, i) => (
                      <div key={i}>
                        <div className="script-line-name">
                          <span className={`role-dot ${line.color}`} />
                          {line.name}
                        </div>
                        <p>{line.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            </div>
            <div className={`source-backdrop${sourceOpen ? " show" : ""}`} onClick={closeSourceDrawer} />
            <aside className={`detail-source${sourceOpen ? " show" : ""}`} onClick={(e) => e.stopPropagation()}>
              <div className="source-title-row">
                <div className="source-title">来源</div>
                <button className="detail-icon-btn" style={{ width: 32, height: 32 }} type="button" onClick={closeSourceDrawer} title="关闭">
                  <X size={16} />
                </button>
              </div>
              {detailData.file ? (
                <div className="source-file">
                  <div className="source-file-name"><FileText size={16} /><span>{detailData.file}</span></div>
                  <div className="source-file-type"><File size={12} /><span>{detailData.type}</span></div>
                </div>
              ) : null}
              {detailData.desc ? <p className="source-desc">{detailData.desc}</p> : null}
              {/* <div className="source-divider" />
              <div className="source-report">
                <h3>大纲内容</h3>
                <p>{detailData.reportLead}</p>
                <ul>
                  {detailData.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div> */}
            </aside>
          </section>
        )}
      </div>

      <AppToast config={toastVisible ? { type: "info", message: toastText, closable: false } : null} onClose={() => setToastVisible(false)} />
    </div>
  );
};
